import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { withStyles } from '@material-ui/core/styles';
import { Constants } from 'utilities/constants';
import NameInput from './NameInput';
import GroupFields from './GroupFields';
import { LoadingOverlayWrapper } from 'components/layout';
import GroupShapes from './GroupShapes';
import {
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  SvgIcon
} from '@material-ui/core';
import {
  setConfirmationModalState,
  updateAssumptionGroup,
  deleteAssumptionGroup,
  setSelectedGroupIsOpen,
  setCreatingNewShape,
  setCustomShapes,
  setShowFieldValueGrid,
  setShowCustomShapes,
  setCopyShapesModalOpen,
  publishAssumptionGroup
} from 'store/actions';
import BulkPasteModal from 'components/assumptions/BulkPasteModal';
import { format } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';

const Group = ({
  classes,
  gridRef,
  // props
  user,
  selectedModuleName,
  selectedGroup,
  selectedGroupIsLoading,
  showFieldValueGrid,
  showCustomShapes,
  // actions
  setConfirmationModalState,
  updateAssumptionGroup,
  deleteAssumptionGroup,
  setSelectedGroupIsOpen,
  setShowFieldValueGrid,
  setShowCustomShapes,
  setCopyShapesModalOpen,
  publishAssumptionGroup,
}) => {
  // == Constants ====================================

  const fieldValueSwitchDisabled =
    selectedGroup && selectedGroup.shapes.length === 0;
  const customShapeSwitchDisabled =
    selectedGroup && selectedGroup.shapes.length === 0;

  // == useState =====================================

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isBulkPasteModalOpen, setIsBulkPasteModalOpen] = useState(false);

  // == Event Handlers ===============================

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  };

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleRenameGroupClick = (e) => {
    closeMoreMenu();
    setIsEditingName(true);
  };

  const handleNameSave = (name) => {
    updateAssumptionGroup({
      ...selectedGroup,
      name: name,
    });

    setIsEditingName(false);
  };

  const handleImportShapesClick = () => {
    closeMoreMenu();
    setCopyShapesModalOpen(true);
  };

  const handlePublishClick = () => {
    closeMoreMenu();
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();
        publishAssumptionGroup({ selectedGroup });
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to publish ${selectedGroup.name}?`,
    });
  };

  const handleDeleteGroupClick = () => {
    closeMoreMenu();
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();
        deleteAssumptionGroup(selectedGroup.groupId);
        setSelectedGroupIsOpen(false);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete ${selectedGroup.name}?`,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  const handleBackToGroups = () => {
    setShowCustomShapes(false);
    setSelectedGroupIsOpen(false);
    setCustomShapes([]);
  };

  const handleBulkPasteClick = () => {
    setIsBulkPasteModalOpen(true);
  }

  const handleExcelExport = (e) => {
    const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
    const fileName = `${selectedModuleName} - ${selectedGroup.name} ${timestamp}`;
    dataGridExtension.exportToExcel(gridRef, fileName);
  }

  // == Components ======================================================

  return (
    <div className={classes.groupContainer}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Tooltip title='Back to groups'>
            <span>
              <IconButton
                onClick={() => handleBackToGroups()}
                size='small'
                color='inherit'
                classes={{
                  root: classes.iconButton,
                  disabled: classes.headerIconDisabled,
                }}
                style={{ margin: '0 4px' }}
              >
                <ArrowBackIcon />
              </IconButton>
            </span>
          </Tooltip>
          {isEditingName ? (
            <NameInput
              name={selectedGroup?.name}
              invertColor
              onSave={handleNameSave}
            />
          ) : (
            <Typography
              variant='subtitle2'
              className={classes.headerText}
              color='inherit'
              onDoubleClick={handleRenameGroupClick}
            >
              {selectedGroup?.name}
            </Typography>
          )}
        </div>
        <div className={classes.headerRight}>
          <IconButton
            disabled={selectedGroup?.wasPublished || selectedGroupIsLoading}
            className={classes.iconButton}
            onClick={openMoreMenu}
            size='small'
            color='inherit'
            classes={{
              root: classes.iconButton,
              disabled: classes.headerIconDisabled,
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchorEl}
            open={Boolean(moreMenuAnchorEl)}
            onClose={closeMoreMenu}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
          >
            <MenuItem onClick={handleRenameGroupClick}>Rename Group</MenuItem>
            <MenuItem onClick={handleDeleteGroupClick}>Delete Group</MenuItem>
            <MenuItem onClick={handleImportShapesClick}>Import Shapes</MenuItem>
            <MenuItem onClick={handlePublishClick}>Publish</MenuItem>
          </Menu>
        </div>
      </div>

      <LoadingOverlayWrapper
        className={classes.overlay}
        active={selectedGroupIsLoading}
        message='Loading group...'
      >
        <GroupFields />
        <GroupShapes />
      </LoadingOverlayWrapper>
      <div style={{ flexGrow: 1 }} />
      <div className={classes.controlsContainer}>
        <div style={{ width: '100%' }}>
          <div className={classes.switchContainer}>
            <Tooltip
              title={fieldValueSwitchDisabled ? 'Create a shape to enable' : ''}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      selectedGroup?.shapes.length > 0 && showFieldValueGrid
                    }
                    onChange={(e) =>
                      setShowFieldValueGrid(e.currentTarget.checked)
                    }
                  />
                }
                label='Show data grid'
                disabled={fieldValueSwitchDisabled || selectedGroupIsLoading}
              />
            </Tooltip>
          </div>
          <div className={classes.switchContainer}>
            <Tooltip
              title={customShapeSwitchDisabled ? 'Create a shape to enable' : ''}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedGroup?.shapes.length > 0 && showCustomShapes}
                    onChange={(e) => setShowCustomShapes(e.currentTarget.checked)}
                  />
                }
                label='Show custom shapes'
                disabled={customShapeSwitchDisabled || selectedGroupIsLoading}
              />
            </Tooltip>
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <Tooltip title='Bulk grid edit'>
            <IconButton 
              style={{ height: 'min-content' }}
              onClick={handleBulkPasteClick}
              disabled={selectedGroup?.wasPublished}
            >
              <SvgIcon>
                <path d="M19,20H5V4H7V7H17V4H19M12,2A1,1 0 0,1 13,3A1,1 0 0,1 12,4A1,1 0 0,1 11,3A1,1 0 0,1 12,2M19,2H14.82C14.4,0.84 13.3,0 12,0C10.7,0 9.6,0.84 9.18,2H5A2,2 0 0,0 3,4V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V4A2,2 0 0,0 19,2Z" />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Export grid to Excel">
            <IconButton
              style={{ height: 'min-content' }}
              onClick={handleExcelExport}
            >
              <icon className='dx-icon-exportxlsx' style={{ fontSize: '1.5rem' }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <BulkPasteModal open={isBulkPasteModalOpen} onClose={() => setIsBulkPasteModalOpen(false)}/>
    </div>
  );
};

// == Styles =====================================================

const styles = {
  groupContainer: {
    flexShrink: 0,
    backgroundColor: '#f2f2f2',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
    minWidth: 0,
  },
  headerRight: {
    marginRight: 4,
    display: 'flex',
  },
  headerText: {
    fontSize: '1.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconButton: {
    padding: '5px',
  },
  controlsContainer: {
    padding: '8px 24px',
    marginTop: -1,
    borderTop: '1px solid #dddddd',
    zIndex: 1,
    display: 'flex'
  },
  switchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  overlay: {
    height: '100%',
    overflow: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center'
  }
};

const mapStateToProps = ({ assumptionModulesState, assumptionGroupsState, appState }) => {
  return {
    user: appState.user,
    selectedModuleName: assumptionModulesState.selectedModule.name,
    selectedGroup: assumptionGroupsState.selectedGroup,
    selectedGroupIsLoading: assumptionGroupsState.selectedGroupIsLoading,
    showFieldValueGrid: assumptionGroupsState.showFieldValueGrid,
    showCustomShapes: assumptionGroupsState.showCustomShapes,
  };
};

const mapDispatchToProps = {
  setConfirmationModalState,
  updateAssumptionGroup,
  deleteAssumptionGroup,
  setSelectedGroupIsOpen,
  setCreatingNewShape,
  setShowFieldValueGrid,
  setShowCustomShapes,
  setCopyShapesModalOpen,
  publishAssumptionGroup,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(Group);
